// AGC: QuoteBuilderModal - Solicitar cotação flow: items → review & save/share
// AGC: No supplier step — cotações are generic requests sent to multiple suppliers for pricing

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { X, Package, Plus, Trash2, ChevronDown, ChevronRight, Send, Loader2, ClipboardList, Search, Layers, ShoppingCart, Save, MapPin, Check, Filter } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { clampDecimals } from '../../utils'
import { useFarms } from '../../context'
import {
  shareQuote,
  quoteLineToBOMItem,
  type QuoteLine,
  type TaskQuoteOption,
  type QuoteTranslations,
} from '../../services/quotePdfService'
import { createQuoteRequest, sendQuoteRequest } from '../../services/quoteService'
import toast from 'react-hot-toast'

const UNIT_OPTIONS = [
  { value: 'L', label: 'L' },
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'sacas', label: 'Sacas' },
  { value: 'unidades', label: 'Unidades' },
  { value: 'doses', label: 'Doses' },
]

const normalizeName = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '')

export interface QuoteAllMissingItem {
  inputId: string
  name: string
  unit: string
  needToBuy: number
}

export interface QuoteAllItem {
  inputId: string
  name: string
  unit: string
}

export interface QuoteBuilderModalProps {
  isOpen: boolean
  onClose: () => void
  farmName: string
  farmLocation?: { lat: number; lng: number }
  lotName: string
  lotArea: number
  tasksWithMissing: TaskQuoteOption[]
  allTaskOptions: TaskQuoteOption[]
  allMissingItems: QuoteAllMissingItem[]
  allItems: QuoteAllItem[]
  getQuoteTranslations: () => QuoteTranslations
  onQuoteCreated?: (quoteId: string) => void
}

export const QuoteBuilderModal: React.FC<QuoteBuilderModalProps> = ({
  isOpen,
  onClose,
  farmName,
  farmLocation,
  lotName,
  lotArea,
  tasksWithMissing,
  allTaskOptions,
  allMissingItems,
  allItems,
  getQuoteTranslations,
  onQuoteCreated,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { currentFarm } = useFarms()
  const [step, setStep] = useState<'items' | 'review'>('items')
  const [addItemTab, setAddItemTab] = useState<'all' | 'task' | 'missing'>('all')
  const [taskSubTab, setTaskSubTab] = useState<'tarefa' | 'talhao'>('tarefa')
  const [onlyNecessary, setOnlyNecessary] = useState(false)
  const [expandedTaskKey, setExpandedTaskKey] = useState<string | null>(null)
  const [expandedLotKey, setExpandedLotKey] = useState<string | null>(null)
  const [allItemsSearch, setAllItemsSearch] = useState('')
  const [lines, setLines] = useState<QuoteLine[]>([])
  const [selectedTaskKeys, setSelectedTaskKeys] = useState<Set<string>>(new Set())
  const [selectedLotKeys, setSelectedLotKeys] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setStep('items')
      setAddItemTab('all')
      setTaskSubTab('tarefa')
      setOnlyNecessary(false)
      setExpandedTaskKey(null)
      setExpandedLotKey(null)
      setAllItemsSearch('')
      setLines([])
      setSelectedTaskKeys(new Set())
      setSelectedLotKeys(new Set())
    }
  }, [isOpen])

  const lineKeys = useMemo(() => new Set(
    lines.map(l => l.inputId || normalizeName(l.inputName))
  ), [lines])

  const selectedTaskQuantities = useMemo(() => {
    const map = new Map<string, number>()
    tasksWithMissing
      .filter(opt => selectedTaskKeys.has(`${opt.lotId}-${opt.taskId}`))
      .forEach(opt => {
        opt.inputs.forEach(inp => {
          const key = inp.inputId || normalizeName(inp.inputName)
          map.set(key, (map.get(key) || 0) + inp.needToBuy)
        })
      })
    return map
  }, [tasksWithMissing, selectedTaskKeys])

  const missingNotInLines = useMemo(() => {
    return allMissingItems
      .filter(m => !lineKeys.has(m.inputId || normalizeName(m.name)))
      .map(m => {
        const key = m.inputId || normalizeName(m.name)
        const claimed = selectedTaskQuantities.get(key) || 0
        const adjusted = Math.max(0, m.needToBuy - claimed)
        return { ...m, needToBuy: adjusted }
      })
      .filter(m => m.needToBuy > 0)
  }, [allMissingItems, lineKeys, selectedTaskQuantities])

  const allItemsFiltered = useMemo(() => {
    const q = allItemsSearch.trim().toLowerCase()
    if (!q) return allItems.slice(0, 100)
    return allItems.filter(i => i.name.toLowerCase().includes(q) || (i.inputId && i.inputId.toLowerCase().includes(q)))
  }, [allItems, allItemsSearch])

  // AGC: Active task list — switches between all tasks and only tasks with missing inputs
  const activeTaskList = useMemo(() => onlyNecessary ? tasksWithMissing : allTaskOptions, [onlyNecessary, tasksWithMissing, allTaskOptions])

  // AGC: Regroup by lotId for the Talhão subtab
  const itemsByLot = useMemo(() => {
    const lotMap = new Map<string, { lotName: string; inputs: Map<string, { inputId: string; inputName: string; unit: string; totalNeedToBuy: number }> }>()
    activeTaskList.forEach(opt => {
      let lot = lotMap.get(opt.lotId)
      if (!lot) {
        lot = { lotName: opt.lotName, inputs: new Map() }
        lotMap.set(opt.lotId, lot)
      }
      opt.inputs.forEach(inp => {
        const key = inp.inputId || normalizeName(inp.inputName)
        const existing = lot!.inputs.get(key)
        if (existing) {
          existing.totalNeedToBuy += inp.needToBuy
        } else {
          lot!.inputs.set(key, { inputId: inp.inputId, inputName: inp.inputName, unit: inp.unit, totalNeedToBuy: inp.needToBuy })
        }
      })
    })
    return Array.from(lotMap.entries()).map(([lotId, data]) => ({
      lotId,
      lotName: data.lotName,
      inputs: Array.from(data.inputs.values()),
    }))
  }, [activeTaskList])

  const handleAddLine = useCallback(() => {
    setLines(prev => [
      ...prev,
      { id: `line-${Date.now()}-${prev.length}`, inputName: '', quantity: 0, unit: 'L' },
    ])
  }, [])

  const updateLine = useCallback((id: string, field: keyof QuoteLine, value: string | number) => {
    setLines(prev => prev.map(l => (l.id === id ? { ...l, [field]: value } : l)))
  }, [])

  const removeLine = useCallback((id: string) => {
    setLines(prev => prev.filter(l => l.id !== id))
  }, [])

  const toggleTask = useCallback((key: string) => {
    setSelectedTaskKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleLot = useCallback((lotId: string) => {
    setSelectedLotKeys(prev => {
      const next = new Set(prev)
      if (next.has(lotId)) next.delete(lotId)
      else next.add(lotId)
      return next
    })
  }, [])

  const handleAddOneItem = useCallback((inputId: string, name: string, unit: string, quantity: number) => {
    setLines(prev => {
      const key = inputId || normalizeName(name)
      const existing = prev.find(l => (l.inputId || normalizeName(l.inputName)) === key)
      if (existing) {
        return prev.map(l => l.id === existing.id ? { ...l, quantity: l.quantity + quantity } : l)
      }
      return [...prev, { id: `line-${Date.now()}-${key}`, inputId, inputName: name, quantity, unit }]
    })
  }, [])

  const handleAddFromTasks = useCallback(() => {
    const selected = activeTaskList.filter(opt => selectedTaskKeys.has(`${opt.lotId}-${opt.taskId}`))
    if (selected.length === 0) {
      toast.error(t('inventory.quoteBuilder.selectAtLeastOneTask') || 'Selecione pelo menos uma tarefa')
      return
    }
    setLines(prev => {
      const byKey = new Map<string, QuoteLine>()
      prev.forEach(l => {
        const k = l.inputId || normalizeName(l.inputName)
        byKey.set(k, { ...l })
      })
      selected.forEach(opt => {
        opt.inputs.forEach(inp => {
          const key = inp.inputId || normalizeName(inp.inputName)
          const existing = byKey.get(key)
          if (existing) {
            byKey.set(key, { ...existing, quantity: existing.quantity + inp.needToBuy })
          } else {
            byKey.set(key, {
              id: `line-${Date.now()}-${inp.inputId}-${key}`,
              inputId: inp.inputId,
              inputName: inp.inputName,
              quantity: inp.needToBuy,
              unit: inp.unit,
            })
          }
        })
      })
      return Array.from(byKey.values())
    })
    setSelectedTaskKeys(new Set())
    toast.success(t('inventory.quoteBuilder.tasksAdded') || 'Itens adicionados')
  }, [activeTaskList, selectedTaskKeys, t])

  // AGC: Add from talhão subtab — merge inputs from all tasks for selected lots
  const handleAddFromLots = useCallback(() => {
    const selected = itemsByLot.filter(lot => selectedLotKeys.has(lot.lotId))
    if (selected.length === 0) {
      toast.error(t('inventory.quoteBuilder.selectAtLeastOneTask') || 'Selecione pelo menos um talhão')
      return
    }
    setLines(prev => {
      const byKey = new Map<string, QuoteLine>()
      prev.forEach(l => {
        const k = l.inputId || normalizeName(l.inputName)
        byKey.set(k, { ...l })
      })
      selected.forEach(lot => {
        lot.inputs.forEach(inp => {
          const key = inp.inputId || normalizeName(inp.inputName)
          const existing = byKey.get(key)
          if (existing) {
            byKey.set(key, { ...existing, quantity: existing.quantity + inp.totalNeedToBuy })
          } else {
            byKey.set(key, {
              id: `line-${Date.now()}-${inp.inputId}-${key}`,
              inputId: inp.inputId,
              inputName: inp.inputName,
              quantity: inp.totalNeedToBuy,
              unit: inp.unit,
            })
          }
        })
      })
      return Array.from(byKey.values())
    })
    setSelectedLotKeys(new Set())
    toast.success(t('inventory.quoteBuilder.tasksAdded') || 'Itens adicionados')
  }, [itemsByLot, selectedLotKeys, t])

  const canProceedFromItems = lines.some(l => (l.inputName || '').trim() && l.quantity > 0) || selectedTaskKeys.size > 0 || selectedLotKeys.size > 0

  // AGC: Auto-add selected tasks/lots before proceeding to review
  const handleProceedToReview = useCallback(() => {
    if (selectedTaskKeys.size > 0) handleAddFromTasks()
    if (selectedLotKeys.size > 0) handleAddFromLots()
    setStep('review')
  }, [selectedTaskKeys, selectedLotKeys, handleAddFromTasks, handleAddFromLots])

  // AGC: Save quote as draft (no sharing)
  const handleSaveDraft = useCallback(async () => {
    const validLines = lines.filter(l => (l.inputName || '').trim() && l.quantity > 0)
    if (validLines.length === 0) return
    if (!user?.uid || !currentFarm?.id) return
    setIsSaving(true)
    try {
      const quoteItems = validLines.map(l => ({
        inputId: l.inputId || '',
        inputName: l.inputName,
        unit: l.unit,
        requestedQuantity: l.quantity,
      }))
      const quote = await createQuoteRequest(user.uid, currentFarm.id, { items: quoteItems })
      toast.success(t('inventory.quoteBuilder.savedSuccess') || 'Cotação salva!')
      onQuoteCreated?.(quote.id)
      onClose()
    } catch (error) {
      console.error('AGC: Error saving quote:', error)
      toast.error(t('inventory.quoteBuilder.sentError') || 'Erro ao salvar cotação')
    } finally {
      setIsSaving(false)
    }
  }, [lines, user, currentFarm, t, onQuoteCreated, onClose])

  // AGC: Save quote + share via WhatsApp
  const handleShareAndSave = useCallback(async () => {
    const validLines = lines.filter(l => (l.inputName || '').trim() && l.quantity > 0)
    if (validLines.length === 0) {
      toast.error(t('inventory.quoteBuilder.atLeastOneLine') || 'Adicione pelo menos um item')
      return
    }
    if (!user?.uid || !currentFarm?.id) return
    setIsSaving(true)
    const translations = getQuoteTranslations()
    try {
      const quoteItems = validLines.map(l => ({
        inputId: l.inputId || '',
        inputName: l.inputName,
        unit: l.unit,
        requestedQuantity: l.quantity,
      }))
      const quote = await createQuoteRequest(user.uid, currentFarm.id, { items: quoteItems })

      const quoteData = {
        farmName,
        farmLocation,
        lotName,
        lotArea,
        items: validLines.map(quoteLineToBOMItem),
        date: new Date(),
        filterType: 'all' as const,
      }
      await shareQuote(quoteData, { ...translations, supplierInfo: translations.supplierInfo ?? t('inputs.orders.supplier.title') })

      await sendQuoteRequest(user.uid, currentFarm.id, quote.id, 'whatsapp')
      toast.success(t('inventory.quoteBuilder.sentSuccess') || 'Cotação enviada!')
      onQuoteCreated?.(quote.id)
      onClose()
    } catch (error) {
      console.error('AGC: Error sharing quote:', error)
      toast.error(t('inventory.quoteBuilder.sentError') || 'Erro ao enviar cotação')
    } finally {
      setIsSaving(false)
    }
  }, [lines, farmName, farmLocation, lotName, lotArea, user, currentFarm, getQuoteTranslations, onQuoteCreated, onClose, t])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center pt-4 sm:pt-0"
      onClick={(e) => e.target === e.currentTarget && !isSaving && onClose()}
    >
      <div
        className="bg-white dark:bg-slate-800 w-full sm:w-[500px] sm:max-h-[85vh] max-h-[85vh] rounded-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-2 sm:mx-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* AGC: Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 px-4 py-4 z-10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {t('inventory.quoteBuilder.title') || 'Solicitar cotação'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {step === 'items' && (t('inventory.quoteBuilder.stepItems') || 'Itens')}
                  {step === 'review' && (t('inventory.quoteBuilder.stepReviewSave') || 'Revisar e salvar')}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} disabled={isSaving} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* AGC: Step indicators — 2 steps only */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          {(['items', 'review'] as const).map((s, idx) => (
            <button
              key={s}
              type="button"
              onClick={() => setStep(s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                step === s ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === s ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                {idx + 1}
              </span>
              {s === 'items' && (t('inventory.quoteBuilder.stepItems') || 'Itens')}
              {s === 'review' && (t('inventory.quoteBuilder.stepReviewSave') || 'Revisar')}
            </button>
          ))}
        </div>

        {/* AGC: Content */}
        {step === 'items' && (
          <>
            {/* AGC: Sticky tabs + search — stays visible when keyboard opens */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 px-4 pt-3 pb-2 space-y-2 border-b border-gray-100 dark:border-gray-700/50 flex-shrink-0">
              {/* AGC: 3 sub-tabs: Todos (default) → Tarefa → Faltando */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-slate-700/50 rounded-xl">
                <button
                  type="button"
                  onClick={() => setAddItemTab('all')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium ${addItemTab === 'all' ? 'bg-white dark:bg-slate-700 shadow text-emerald-700 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  {t('inventory.quoteBuilder.tabAllItems') || 'Todos'}
                </button>
                <button
                  type="button"
                  onClick={() => setAddItemTab('task')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium ${addItemTab === 'task' ? 'bg-white dark:bg-slate-700 shadow text-emerald-700 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  <ClipboardList className="w-3.5 h-3.5" />
                  {t('inventory.quoteBuilder.tabTask') || 'Tarefa'}
                </button>
                <button
                  type="button"
                  onClick={() => setAddItemTab('missing')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium ${addItemTab === 'missing' ? 'bg-white dark:bg-slate-700 shadow text-emerald-700 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  {t('inventory.quoteBuilder.tabAllMissing') || 'Faltando'}
                </button>
              </div>

              {/* AGC: Tarefa subtabs + "somente necessário" toggle */}
              {addItemTab === 'task' && (
                <div className="flex items-center gap-1 justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setTaskSubTab('tarefa')}
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${taskSubTab === 'tarefa' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                    >
                      {t('inventory.quoteBuilder.subTabTarefa') || 'Tarefa'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTaskSubTab('talhao')}
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${taskSubTab === 'talhao' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                    >
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {t('inventory.quoteBuilder.subTabTalhao') || 'Talhão'}
                      </span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOnlyNecessary(prev => !prev)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium ${onlyNecessary ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                  >
                    <Filter className="w-3 h-3" />
                    {t('inventory.quoteBuilder.onlyNecessary') || 'Só necessário'}
                  </button>
                </div>
              )}

              {/* AGC: Search bar for Todos tab */}
              {addItemTab === 'all' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={allItemsSearch}
                    onChange={(e) => setAllItemsSearch(e.target.value)}
                    placeholder={t('inventory.quoteBuilder.searchAllPlaceholder') || 'Buscar por nome ou código...'}
                    className="w-full pl-9 pr-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>

            {/* AGC: Scrollable items */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2 space-y-3">
              {/* AGC: Tab: Todos — searchable list with added-state feedback */}
              {addItemTab === 'all' && (
                <div className="space-y-1 border border-gray-200 dark:border-slate-600 rounded-xl p-2">
                  {allItemsFiltered.slice(0, 50).map(item => {
                    const itemKey = item.inputId || normalizeName(item.name)
                    const addedLine = lines.find(l => (l.inputId || normalizeName(l.inputName)) === itemKey)
                    const isAdded = !!addedLine
                    return (
                      <div key={item.inputId || item.name} className={`flex items-center justify-between gap-2 p-2 rounded-lg ${isAdded ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {isAdded && <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />}
                          <span className={`text-sm truncate ${isAdded ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-gray-900 dark:text-white'}`}>{item.name}</span>
                        </div>
                        {isAdded ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={addedLine!.quantity || ''}
                              onChange={(e) => updateLine(addedLine!.id, 'quantity', clampDecimals(parseFloat(e.target.value) || 0))}
                              className="w-16 px-2 py-1 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-slate-700 text-sm text-right text-gray-900 dark:text-white"
                            />
                            <span className="text-xs text-gray-500">{item.unit}</span>
                            <button
                              type="button"
                              onClick={() => removeLine(addedLine!.id)}
                              className="p-1 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleAddOneItem(item.inputId, item.name, item.unit, 1)}
                            className="shrink-0 p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-800/50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {allItemsFiltered.length > 50 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 py-1">({t('inventory.quoteBuilder.showingFirst50') || 'Mostrando os 50 primeiros'})</p>
                  )}
                </div>
              )}

              {/* AGC: Tab: Tarefa — subtab Tarefa: grouped by task */}
              {addItemTab === 'task' && taskSubTab === 'tarefa' && (
                <div className="space-y-2">
                  {activeTaskList.length > 0 ? (
                    <>
                      <div className="space-y-1 border border-gray-200 dark:border-slate-600 rounded-xl p-2">
                        {activeTaskList.map(opt => {
                          const key = `${opt.lotId}-${opt.taskId}`
                          const checked = selectedTaskKeys.has(key)
                          const expanded = expandedTaskKey === key
                          return (
                            <div key={key} className="rounded-lg overflow-hidden border border-gray-100 dark:border-slate-600">
                              <label className={`flex items-center gap-2 p-2 cursor-pointer ${checked ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleTask(key)}
                                  className="rounded border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setExpandedTaskKey(expanded ? null : key) }}
                                  className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                                >
                                  <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{opt.taskTitle}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">/ {opt.lotName}</span>
                                </button>
                              </label>
                              {expanded && (
                                <div className="px-2 pb-2 pt-0 pl-8 space-y-1 border-t border-gray-100 dark:border-slate-600">
                                  {opt.inputs.map(i => (
                                    <p key={i.inputId || i.inputName} className="text-xs text-gray-600 dark:text-gray-400">
                                      {i.inputName}: {i.needToBuy} {i.unit}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={handleAddFromTasks}
                        className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        {t('inventory.quoteBuilder.addSelected') || 'Adicionar selecionados'}
                      </button>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('inventory.quoteBuilder.noTasksWithMissing') || 'Nenhuma tarefa com insumos em falta. Adicione itens manualmente.'}
                    </p>
                  )}
                </div>
              )}

              {/* AGC: Tab: Tarefa — subtab Talhão: grouped by lot */}
              {addItemTab === 'task' && taskSubTab === 'talhao' && (
                <div className="space-y-2">
                  {itemsByLot.length > 0 ? (
                    <>
                      <div className="space-y-1 border border-gray-200 dark:border-slate-600 rounded-xl p-2">
                        {itemsByLot.map(lot => {
                          const checked = selectedLotKeys.has(lot.lotId)
                          const expanded = expandedLotKey === lot.lotId
                          return (
                            <div key={lot.lotId} className="rounded-lg overflow-hidden border border-gray-100 dark:border-slate-600">
                              <label className={`flex items-center gap-2 p-2 cursor-pointer ${checked ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleLot(lot.lotId)}
                                  className="rounded border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setExpandedLotKey(expanded ? null : lot.lotId) }}
                                  className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                                >
                                  <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                                  <MapPin className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{lot.lotName}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">({lot.inputs.length})</span>
                                </button>
                              </label>
                              {expanded && (
                                <div className="px-2 pb-2 pt-0 pl-8 space-y-1 border-t border-gray-100 dark:border-slate-600">
                                  {lot.inputs.map(i => (
                                    <p key={i.inputId || i.inputName} className="text-xs text-gray-600 dark:text-gray-400">
                                      {i.inputName}: {i.totalNeedToBuy} {i.unit}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={handleAddFromLots}
                        className="text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        {t('inventory.quoteBuilder.addSelected') || 'Adicionar selecionados'}
                      </button>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('inventory.quoteBuilder.noTasksWithMissing') || 'Nenhuma tarefa com insumos em falta.'}
                    </p>
                  )}
                </div>
              )}

              {/* AGC: Tab: Faltando — all missing items */}
              {addItemTab === 'missing' && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('inventory.quoteBuilder.tabMissingHint') || 'Itens já adicionados por tarefa não aparecem aqui.'}
                  </p>
                  <div className="space-y-1 border border-gray-200 dark:border-slate-600 rounded-xl p-2">
                    {missingNotInLines.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 py-2">{t('inventory.quoteBuilder.noMissingToAdd') || 'Nenhum item em falta para adicionar.'}</p>
                    ) : (
                      missingNotInLines.map(m => (
                        <div key={m.inputId || m.name} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{m.needToBuy} {m.unit}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddOneItem(m.inputId, m.name, m.unit, m.needToBuy)}
                            className="shrink-0 p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-800/50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* AGC: Manual add line */}
              <button
                type="button"
                onClick={handleAddLine}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-sm"
              >
                <Plus className="w-4 h-4" />
                {t('inventory.quoteBuilder.addLine') || 'Adicionar item'}
              </button>

              {/* AGC: Lines list */}
              {lines.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{lines.length} {lines.length === 1 ? 'item' : 'itens'}</p>
                  {lines.map(line => (
                    <div
                      key={line.id}
                      className="grid grid-cols-12 gap-2 items-center p-2 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-600"
                    >
                      <input
                        type="text"
                        value={line.inputName}
                        onChange={(e) => updateLine(line.id, 'inputName', e.target.value)}
                        placeholder={t('inventory.itemName') || 'Nome'}
                        className="col-span-4 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
                      />
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={line.quantity || ''}
                        onChange={(e) => updateLine(line.id, 'quantity', clampDecimals(parseFloat(e.target.value) || 0))}
                        placeholder="0"
                        className="col-span-3 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
                      />
                      <select
                        value={line.unit}
                        onChange={(e) => updateLine(line.id, 'unit', e.target.value)}
                        className="col-span-3 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
                      >
                        {UNIT_OPTIONS.map(u => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeLine(line.id)}
                        className="col-span-2 p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {step === 'review' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>{farmName}</strong> · {lotName} ({lotArea.toFixed(2)} ha)
              </p>
            </div>
            <div className="space-y-2">
              {lines.filter(l => (l.inputName || '').trim()).map(l => (
                <div key={l.id} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">{l.inputName}</span>
                  </div>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={l.quantity || ''}
                    onChange={(e) => updateLine(l.id, 'quantity', clampDecimals(parseFloat(e.target.value) || 0))}
                    className="w-20 px-2 py-1 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-right text-gray-900 dark:text-white"
                  />
                  <span className="text-xs text-gray-500 w-8">{l.unit}</span>
                  <button
                    type="button"
                    onClick={() => removeLine(l.id)}
                    className="p-1 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AGC: Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 px-4 py-4 z-10 flex-shrink-0 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
          >
            {t('common.cancel') || 'Cancelar'}
          </button>
          {step === 'items' && (
            <button
              type="button"
              onClick={handleProceedToReview}
              disabled={!canProceedFromItems}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {t('common.next') || 'Próximo'}
              <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </button>
          )}
          {step === 'review' && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                <Save className="w-4 h-4" />
                {t('inventory.quoteBuilder.saveDraft') || 'Salvar'}
              </button>
              <button
                type="button"
                onClick={handleShareAndSave}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t('inventory.quoteBuilder.shareAndSave') || 'Compartilhar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuoteBuilderModal
